/**
 * Read-only access-token claim decoding.
 *
 * The browser NEVER validates a token — signature verification is the API
 * gateway's job (`services/gateway/src/jwt.ts`). The client only needs the
 * claims to pick a portal, show the account menu and know when to refresh, so
 * this decodes the payload without any cryptographic trust.
 */
import { isPersona, type Persona } from './personas';

/** Subset of `AccessTokenClaims` (services/auth/src/domain/tokens.ts) the UI reads. */
export interface AccessTokenClaims {
  sub: string;
  /** Tenant id — `null` for the fanatic persona. */
  tid: string | null;
  email: string;
  persona: Persona;
  isMfaVerified: boolean;
  /** The account enforces MFA (`users.mfa_enforced`) — the gateway gates on this + `isMfaVerified`. */
  isMfaRequired: boolean;
  sessionId: string | null;
  /** Expiry as epoch milliseconds. */
  expiresAtMs: number;
}

function decodeBase64Url(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='));
  // Recover UTF-8 (e.g. non-ASCII display names) from the latin1 byte string.
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Decode the claims the UI needs, or `null` when the token is unreadable.
 * Never throws — a malformed token is treated as "not signed in".
 */
export function decodeAccessToken(token: string): AccessTokenClaims | null {
  const segments = token.split('.');
  if (segments.length !== 3) return null;
  try {
    const payload: unknown = JSON.parse(decodeBase64Url(segments[1] ?? ''));
    if (typeof payload !== 'object' || payload === null) return null;
    const record = payload as Record<string, unknown>;
    const sub = asString(record.sub);
    const persona = record.persona;
    if (sub === null || !isPersona(persona)) return null;
    return {
      sub,
      tid: asString(record.tid),
      email: asString(record.email) ?? '',
      persona,
      isMfaVerified: record.mfa_verified === true,
      isMfaRequired: record.mfa_required === true,
      sessionId: asString(record.session_id),
      expiresAtMs: typeof record.exp === 'number' ? record.exp * 1000 : 0,
    };
  } catch {
    return null;
  }
}
